import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useEstabrookContext } from "../api/estabrookContext";
import {
  fetchAdminLocationV2ControllerFindMany,
  AdminLocationV2ControllerGetAllLocationsVariables,
  AdminLocationV2ControllerGetAllLocationsResponse,
  AdminLocationV2ControllerGetAllLocationsError,
  fetchAdminRegionV2ControllerGetRegionsPaged,
  AdminRegionV2ControllerGetRegionsVariables,
  AdminRegionV2ControllerGetRegionsResponse,
  AdminRegionV2ControllerGetRegionsError,
  fetchAdminRouteV2ControllerGetRoutesPaged,
  AdminRouteV2ControllerGetRoutesVariables,
  AdminRouteV2ControllerGetRoutesResponse,
  AdminRouteV2ControllerGetRoutesError,
  fetchAdminLocationV2ControllerGetUnassignedRouteLocationsForDate,
  AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables,
  AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
  AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
} from "../api/estabrookComponents";

type PaginatedResponse<T = unknown> = {
  data?: T[];
  metaData?: {
    totalPages: number;
    page: number;
    limit: number;
    count: number;
  };
};

const requestCache = new Map<string, Promise<PaginatedResponse>>();

// Normalize variables by removing undefined values to ensure consistent cache keys
const normalizeVariables = (
  variables: Record<string, unknown>,
): Record<string, unknown> => {
  if (!variables || typeof variables !== "object") return variables;

  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(variables)) {
    if (value !== undefined) {
      if (typeof value === "object" && value !== null) {
        const normalizedNested = normalizeVariables(
          value as Record<string, unknown>,
        );
        if (Object.keys(normalizedNested).length > 0) {
          normalized[key] = normalizedNested;
        }
      } else {
        normalized[key] = value;
      }
    }
  }
  return normalized;
};

const createCacheKey = (
  operationId: string,
  variables: Record<string, unknown>,
): string => {
  const normalizedVariables = normalizeVariables(variables);
  return `${operationId}:${JSON.stringify(normalizedVariables)}`;
};

const fetchAllPages = async <T extends PaginatedResponse>(
  fetchFunction: (
    variables: Record<string, unknown>,
    signal?: AbortSignal,
  ) => Promise<T>,
  variables: Record<string, unknown>,
  fetcherOptions: Record<string, unknown>,
  operationId: string,
  signal?: AbortSignal,
): Promise<T> => {
  const limit = 1000;
  const cacheKey = createCacheKey(operationId, variables);

  // Don't use cached promise if the signal is already aborted
  // This prevents returning aborted promises when queries are invalidated
  if (signal?.aborted) {
    requestCache.delete(cacheKey);
  }

  // Return cached promise if request already in progress AND signal is not aborted
  if (requestCache.has(cacheKey) && !signal?.aborted) {
    return requestCache.get(cacheKey)! as Promise<T>;
  }

  // Listen for abort signal to immediately clear cache
  const abortHandler = () => {
    requestCache.delete(cacheKey);
  };

  if (signal) {
    signal.addEventListener("abort", abortHandler);
  }

  const fetchPromise = (async (): Promise<T> => {
    const allData: unknown[] = [];

    const firstPageVariables = {
      ...variables,
      queryParams: {
        ...(variables.queryParams as Record<string, unknown>),
        page: 1,
        limit,
      },
    };

    const firstPageResponse = await fetchFunction(
      { ...fetcherOptions, ...firstPageVariables },
      signal,
    );

    if (firstPageResponse.data) {
      allData.push(...firstPageResponse.data);
    }

    const totalPages = firstPageResponse.metaData?.totalPages || 1;

    if (totalPages > 1) {
      const remainingPagePromises = [];

      for (let page = 2; page <= totalPages; page++) {
        const pageVariables = {
          ...variables,
          queryParams: {
            ...(variables.queryParams as Record<string, unknown>),
            page,
            limit,
          },
        };

        remainingPagePromises.push(
          fetchFunction({ ...fetcherOptions, ...pageVariables }, signal),
        );
      }

      const remainingResponses = await Promise.all(remainingPagePromises);

      remainingResponses.forEach((response) => {
        if (response.data) {
          allData.push(...response.data);
        }
      });
    }

    return {
      data: allData,
      metaData: {
        totalPages: 1,
        page: 1,
        limit: allData.length,
        count: allData.length,
      },
    } as T;
  })();

  requestCache.set(cacheKey, fetchPromise as Promise<PaginatedResponse>);

  try {
    const result = await fetchPromise;
    requestCache.delete(cacheKey);
    if (signal) {
      signal.removeEventListener("abort", abortHandler);
    }
    return result;
  } catch (error) {
    requestCache.delete(cacheKey);
    if (signal) {
      signal.removeEventListener("abort", abortHandler);
    }
    throw error;
  }
};

// ⚠️ CRITICAL: Use the same query keys as original hooks for cache compatibility
export const useAdminLocationV2ControllerGetAllLocationsPaginated = <
  TData = AdminLocationV2ControllerGetAllLocationsResponse,
>(
  variables: AdminLocationV2ControllerGetAllLocationsVariables,
  options?: Omit<
    UseQueryOptions<
      AdminLocationV2ControllerGetAllLocationsResponse,
      AdminLocationV2ControllerGetAllLocationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);

  return useQuery<
    AdminLocationV2ControllerGetAllLocationsResponse,
    AdminLocationV2ControllerGetAllLocationsError,
    TData
  >({
    // Use EXACT same query key pattern as the original hook for cache compatibility
    queryKey: queryKeyFn({
      path: "/api/v2/admin/locations",
      operationId: "adminLocationV2ControllerGetAllLocations",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAllPages(
        fetchAdminLocationV2ControllerFindMany,
        variables as Record<string, unknown>,
        fetcherOptions,
        "adminLocationV2ControllerGetAllLocations",
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export const useAdminRegionV2ControllerGetRegionsPaginated = <
  TData = AdminRegionV2ControllerGetRegionsResponse,
>(
  variables: AdminRegionV2ControllerGetRegionsVariables,
  options?: Omit<
    UseQueryOptions<
      AdminRegionV2ControllerGetRegionsResponse,
      AdminRegionV2ControllerGetRegionsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);

  return useQuery<
    AdminRegionV2ControllerGetRegionsResponse,
    AdminRegionV2ControllerGetRegionsError,
    TData
  >({
    // Use EXACT same query key pattern as the original hook for cache compatibility
    queryKey: queryKeyFn({
      path: "/api/v2/admin/regions",
      operationId: "adminRegionV2ControllerGetRegions",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAllPages(
        fetchAdminRegionV2ControllerGetRegionsPaged,
        variables as Record<string, unknown>,
        fetcherOptions,
        "adminRegionV2ControllerGetRegions",
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export const useAdminRouteV2ControllerGetRoutesPaginated = <
  TData = AdminRouteV2ControllerGetRoutesResponse,
>(
  variables: AdminRouteV2ControllerGetRoutesVariables,
  options?: Omit<
    UseQueryOptions<
      AdminRouteV2ControllerGetRoutesResponse,
      AdminRouteV2ControllerGetRoutesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useEstabrookContext(options);

  return useQuery<
    AdminRouteV2ControllerGetRoutesResponse,
    AdminRouteV2ControllerGetRoutesError,
    TData
  >({
    // Use EXACT same query key pattern as the original hook for cache compatibility
    queryKey: queryKeyFn({
      path: "/api/v2/admin/routes",
      operationId: "adminRouteV2ControllerGetRoutes",
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchAllPages(
        fetchAdminRouteV2ControllerGetRoutesPaged,
        variables as Record<string, unknown>,
        fetcherOptions,
        "adminRouteV2ControllerGetRoutes",
        signal,
      ),
    ...options,
    ...queryOptions,
  });
};

export const useAdminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated =
  <TData = AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse>(
    variables: AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables,
    options?: Omit<
      UseQueryOptions<
        AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
        AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
        TData
      >,
      "queryKey" | "queryFn" | "initialData"
    >,
  ) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
      useEstabrookContext(options);

    // Create a wrapper to match the expected function signature
    const wrappedFetch = (
      vars: Record<string, unknown>,
      signal?: AbortSignal,
    ) => {
      return fetchAdminLocationV2ControllerGetUnassignedRouteLocationsForDate(
        vars as AdminLocationV2ControllerGetUnassignedRouteLocationsForDateVariables,
        signal,
      );
    };

    return useQuery<
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateResponse,
      AdminLocationV2ControllerGetUnassignedRouteLocationsForDateError,
      TData
    >({
      // Use EXACT same query key pattern as the original hook for cache compatibility
      queryKey: queryKeyFn({
        path: "/api/v2/admin/locations/date/{date}/unassigned",
        operationId:
          "adminLocationV2ControllerGetUnassignedRouteLocationsForDate",
        variables,
      }),
      queryFn: ({ signal }) =>
        fetchAllPages(
          wrappedFetch,
          variables as Record<string, unknown>,
          fetcherOptions,
          "adminLocationV2ControllerGetUnassignedRouteLocationsForDate",
          signal,
        ),
      ...options,
      ...queryOptions,
    });
  };
